import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { my_routes } from "../router/all_routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateParent } from "../../api/parents";
import { toast } from "sonner";
// pour restreaindre l'accès à ce composant
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

enum Sexe {
  Masculin = "Masculin",
  Feminin = "Feminin",
}

const childSchema = z.object({
  id: z.string(),
  value: z.string(),
  label: z.string(),
  relation: z
    .object({ id: z.string(), label: z.string() })
    .nullable()
    .refine((val) => val !== null, { message: "La relation est obligatoire" }),
});

const schema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z.nativeEnum(Sexe),
  isDeceased: z.boolean(),
  email: z.string().optional(),
  number: z.string().optional(),
  adresse: z.string().optional(),
  statut: z.enum(["true", "false"]),
});

type FormFields = z.infer<typeof schema>;

type ParentType = {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  sexe: Sexe;
  number: string;
  adresse: string;
  isDeceased: boolean;
  createdAt: string;
  statut: boolean;
};

const EditParentModal: React.FC<{
  parent: ParentType; // parent existant à modifier
  setAllParents: React.Dispatch<React.SetStateAction<any[]>>;
  updateData: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ parent, setAllParents, updateData }) => {
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({ resolver: zodResolver(schema) });

  const navigate = useNavigate();

  const isDeceasedValue = watch("isDeceased");

  useEffect(() => {
    if (parent) {
      // pré-remplir le formulaire
      setValue("lastname", parent.lastname);
      setValue("firstname", parent.firstname);
      setValue("sexe", parent.sexe);
      setValue("isDeceased", parent.isDeceased);
      setValue("email", parent.email ?? "");
      setValue("number", parent.number ?? "");
      setValue("adresse", parent.adresse ?? "");
      setValue("statut", parent.statut ? "true" : "false");
    }
  }, [parent, setValue]);

  useEffect(() => {
    // Si le statut de décès est "true", on met le statut à "false"
    // car un parent décédé ne peut pas être actif
    if (isDeceasedValue === true) {
      setValue("statut", "false");
      setValue("email", "");
      setValue("adresse", "");
      setValue("number", "");
    } else {
      if (parent) {
        setValue("email", parent.email ?? "");
        setValue("number", parent.number ?? "");
        setValue("adresse", parent.adresse ?? "");
        setValue("statut", parent.statut ? "true" : "false");
      }
    }
  }, [isDeceasedValue, setValue]);

  const onSubmit = async (data: FormFields) => {
    try {
      const formData = {
        lastname: data.lastname,
        firstname: data.firstname,
        sexe: data.sexe,
        isDeceased: data.isDeceased,
        email: data.email || undefined,
        number: data.number || undefined,
        adresse: data.adresse || undefined,
        statut: data.statut === "true",
      };
      await updateParent(parent.id, formData);
      updateData(true);
      setAllParents((prev) =>
        prev.map((p) => (p.id === parent.id ? { ...p, ...formData } : p)),
      );
      const btn = document.getElementById("closeBtn-editParentModal");
      if (btn) {
        btn.click();
      }
      toast.success("Parent modifié");
      navigate(my_routes.allParent, {
        state: { successMessage: "Parent modifié avec succès" },
      });
    } catch (e: any) {
      setError("root", {
        message: e.response?.data?.message || "Erreur inattendue",
      });
    }
  };

  return (
    <div className="modal fade" id="edit_parent_modal">
      <div className="modal-dialog ">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Modifier un Parent</h4>
            <button
              id="closeBtn-editParentModal"
              type="button"
              className="btn btn-sm  btn-danger"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              {errors.root && (
                <div className="alert alert-danger">{errors.root.message}</div>
              )}
              {/* champs parents */}
              <div className="d-block d-xl-flex">
                <div className="mb-3 flex-fill  me-xl-3 me-0">
                  <label className="form-label">Nom</label>
                  <input
                    type="text"
                    placeholder="Entrer un nom"
                    className={`form-control ${
                      errors.lastname ? "is-invalid" : ""
                    }`}
                    {...register("lastname")}
                  />
                  {errors.lastname && (
                    <div className="invalid-feedback">
                      {errors.lastname.message}
                    </div>
                  )}
                </div>
                <div className="mb-3 flex-fill">
                  <label className="form-label">Prénom</label>
                  <input
                    type="text"
                    placeholder="Entrer un prénom"
                    className={`form-control ${
                      errors.firstname ? "is-invalid" : ""
                    }`}
                    {...register("firstname")}
                  />
                  {errors.firstname && (
                    <div className="invalid-feedback">
                      {errors.firstname.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-block d-xl-flex">
                <div className="mb-3 flex-fill me-xl-3 me-0">
                  <label className="form-label">Sexe</label>
                  <select
                    className={`form-select ${errors.sexe ? "is-invalid" : ""}`}
                    {...register("sexe")}
                  >
                    <option value="">Sélectionner le sexe</option>
                    <option value="Masculin">Homme</option>
                    <option value="Feminin">Femme</option>
                  </select>
                  {errors.sexe && (
                    <div className="invalid-feedback">
                      {errors.sexe.message}
                    </div>
                  )}
                </div>

                <div className="m-auto flex-fill ">
                  <div className="form-check form-check-lg form-switch">
                    <label className="form-check-label">Statut du Décès</label>
                    <input
                      {...register("isDeceased")}
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="switch-lg"
                    />
                  </div>
                  {errors.isDeceased && (
                    <div className="invalid-feedback">
                      {errors.isDeceased.message}
                    </div>
                  )}
                </div>
              </div>

              {!isDeceasedValue && (
                <>
                  <div className="d-block d-xl-flex">
                    <div className="mb-3 flex-fill me-xl-3 me-0">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        // disabled={register("isDeceased")}
                        className={`form-control ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        {...register("email")}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">
                          {errors.email.message}
                        </div>
                      )}
                    </div>
                    <div className="mb-3 flex-fill">
                      <label className="form-label">Numéro de téléphone</label>
                      <input
                        type="text"
                        placeholder="Ex. +223 84382001"
                        className={`form-control ${
                          errors.number ? "is-invalid" : ""
                        }`}
                        {...register("number")}
                      />
                      {errors.number && (
                        <div className="invalid-feedback">
                          {errors.number.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Statut</label>
                    <select
                      className={`form-select ${
                        errors.statut ? "is-invalid" : ""
                      }`}
                      {...register("statut")}
                    >
                      <option value="">Sélectionner le statut</option>
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                    {errors.statut && (
                      <div className="invalid-feedback">
                        {errors.statut.message}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Adresse</label>
                    <textarea
                      placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                      rows={4}
                      cols={8}
                      className={`form-control ${
                        errors.adresse ? "is-invalid" : ""
                      }`}
                      {...register("adresse")}
                    />
                    {errors.adresse && (
                      <div className="invalid-feedback">
                        {errors.adresse.message}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ... autres champs ... */}
            </div>
            <div className="modal-footer">
              <Link
                to="#"
                id="cancel-btn"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={() => {
                  reset();
                  clearErrors();
                }}
              >
                Annuler
              </Link>
              {isSubmitting ? (
                <button
                  className="btn btn-primary-light"
                  type="button"
                  disabled={true}
                >
                  <span
                    className="spinner-border spinner-border-sm align-middle me-1 "
                    role="status"
                    aria-hidden="true"
                  />
                  Loading...
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary"
                  // data-bs-dismiss="modal"
                >
                  Editer
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default withPermission(
  EditParentModal,
  [buildPermission(Resource.PARENT, Action.UPDATE)],
  "masquer",
);
